/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Box } from '@primer/react';
import { Dialog } from '@primer/react/experimental';
import { AccessLevel } from '@upright/common/iam/access/types';
import { ModelEntityType } from '@upright/common/model/input/types';
import { CoverageClass } from '@upright/common/model/output/scores/model-entity-data/types';
import {
  NoDrillabilityReason,
  SdgDrillDownTableRow,
} from '@upright/common/model/output/scores/sdg-alignment/api-types';
import {
  SdgAlignmentByLevel,
  SdgAlignmentValence,
  SdgId,
  SdgTarget,
  SdgUnitsMode,
} from '@upright/common/model/output/scores/sdg-alignment/types';
import { useFetchSdgDrillDownData } from '@upright/ui/api/model/output/scores/sdg-alignments';
import Spinner from '@upright/ui/common/generic/components/progress/spinners/spinner';
import { breakpoints } from '@upright/ui/common/generic/global/breakpoints';
import { SdgDrilldownModalMain } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/main';
import { SdgDrillDownModalTitle } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/title';
import { InaccessibleHoldingsBox } from '@upright/ui/views/model-entity-views/groupfolio-view/shared/inaccessible-holdings-overlay-box';
import { compact, get } from 'lodash';
import { SdgModalCaseSingleCause } from './sdg-modal-case-single-cause';
import { SdgModalEmptyState } from './sdg-modal-empty-state';
import { TeamDrillabilityInfo } from './team-sdg-drillability-info';
import { ImpactLensType } from '@upright/common/impact-lenses/types';

interface Props {
  sdgId: SdgId | undefined;
  valence: SdgAlignmentValence | undefined;
  data: SdgAlignmentByLevel | undefined;
  unitsMode: SdgUnitsMode;
  release: string;
  resultId?: number;
  isOpen: boolean;
  onClose: () => void;
  entityType: ModelEntityType.COMPANY | ModelEntityType.GROUP | ModelEntityType.PORTFOLIO;
  entityId: string;
  hasProductViewAccess: boolean;
  coverageClass: CoverageClass | undefined;
  isTeamUser: boolean;
  parentId?: string;
  accessLevel?: AccessLevel;
}

export function SdgDrilldownModal(props: Props) {
  const { sdgId, valence, data, release } = props;

  if (!props.isOpen || !sdgId || !data || !valence) {
    return <></>;
  }

  return (
    <Dialog
      title={
        <SdgDrillDownModalTitle
          unitsMode={props.unitsMode}
          onClose={props.onClose}
          sdgId={sdgId}
          data={data}
          valence={valence}
        />
      }
      width="xlarge"
      sx={{
        [`@media screen and (max-width: ${breakpoints[0]})`]: {
          position: 'absolute',
          bottom: 0,
          width: '100vw',
          maxWidth: '100vw',
        },
      }}
      onClose={props.onClose}
    >
      <SdgDrilldownModalContent {...{ ...props, sdgId, valence, data, release }} />
    </Dialog>
  );
}

const SdgDrilldownModalContent = (
  props: Props & {
    sdgId: SdgId;
    valence: SdgAlignmentValence;
    data: SdgAlignmentByLevel;
  },
) => {
  const { sdgId, valence, resultId, entityType, entityId, release } = props;

  const query = useFetchSdgDrillDownData({ entityType, entityId, sdgId, valence, resultId, release });

  const MainContent = () => {
    if (query.isPending) {
      return <Spinner size={'5em'} />;
    }
    if (query.isError) {
      if (query.error.status === 401) {
        return (
          <Box display="flex" justifyContent="center">
            <InaccessibleHoldingsBox lensTypeToLinkTo={ImpactLensType.SDGS} />
          </Box>
        );
      }
      return <Box>{query.error.message}</Box>;
    }
    if (query.data.drillability.drillable || props.isTeamUser) {
      return <SdgDrilldownModalMain {...props} rows={query.data.rows} />;
    } else {
      const hasSingleAlignedProduct = singleAlignedReasons.includes(query.data.drillability.reason);
      return hasSingleAlignedProduct ? (
        <SdgModalCaseSingleCause entityType={entityType} targets={getSdgTargetsForSingleProduct(query.data.rows)} />
      ) : (
        <SdgModalEmptyState reason={query.data.drillability.reason} />
      );
    }
  };
  return (
    <Box sx={{ fontSize: 0 }}>
      <MainContent />
      {props.isTeamUser && query.data?.drillability.reason && (
        <TeamDrillabilityInfo reason={query.data.drillability.reason} />
      )}
    </Box>
  );
};

const singleAlignedReasons = [
  NoDrillabilityReason.SINGLE_ALIGNED_PRODUCT,
  NoDrillabilityReason.SINGLE_MODERATELY_ALIGNED_PRODUCT,
  NoDrillabilityReason.SINGLE_STRONG_ALIGNED_PRODUCT,
];

function getSdgTargetsForSingleProduct(rows: SdgDrillDownTableRow[]): SdgTarget[] {
  const targets = get(rows, 0, undefined)?.targets;
  if (!targets) {
    return [];
  }
  const contributingTargets = compact(targets.map(target => target.target));
  return contributingTargets;
}
